// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// cues.ts — every sound this game can make, declared in one place.
//
// THE REASON THIS FILE EXISTS: the engine's mixer returns SILENTLY for a cue it does not know.
// A mistyped cue is not an error, it is nothing — so a typo ships as a game with no sound and a
// completely green test suite. tests/source-hygiene.test.ts greps every play() literal in src/ and
// asserts it appears in CUES, then asserts every CUES entry has a patch a real mixer knows (here or
// in the engine's DEFAULT_PATCHES).
//
// Register: this is a quiet game. Nothing here is a stinger. The loudest thing in the mix is the
// auto-pause chime, because that is the one moment the game asks for the player's attention.

import type { Patch } from '@ben-gy/game-engine/sound';

export const CUES = [
  'dig',
  'chop',
  'place',
  'pickup',
  'drop',
  'cook',
  'ill',
  'knell',
  'gust',
  'warm',
  'speed',
  'alert',
  'select',
  'ok',
  'deny',
] as const;

export type Cue = (typeof CUES)[number];

/**
 * Frequencies in hertz. A colony sim's palette is wood, snow and breath, so everything is low and
 * short. `select` deliberately overrides the engine's brighter built-in.
 */
export const PATCHES: Record<string, Patch> = {
  // work
  dig: { type: 'triangle', freq: [180, 120], dur: 0.1, gain: 0.16 },
  chop: { type: 'square', freq: [140, 90], dur: 0.07, gain: 0.14 },
  place: { type: 'triangle', freq: [300, 220], dur: 0.09, gain: 0.15 },
  // hauling — throttled at the call site, because twelve colonists hauling must not be a machine gun
  pickup: { type: 'sine', freq: [420, 520], dur: 0.05, gain: 0.08 },
  drop: { type: 'sine', freq: [320, 240], dur: 0.05, gain: 0.08 },
  cook: { type: 'sine', freq: [520, 700], dur: 0.16, gain: 0.13 },
  warm: { type: 'sine', freq: [220, 330], dur: 0.3, gain: 0.14 },
  // the colony going wrong
  ill: { type: 'sine', freq: [300, 190], dur: 0.34, gain: 0.15 },
  knell: { type: 'sine', freq: [150, 96], dur: 0.9, gain: 0.2 },
  gust: { type: 'sawtooth', freq: [90, 200], dur: 0.5, gain: 0.1, noise: true },
  // the interface
  speed: { type: 'square', freq: [640, 760], dur: 0.04, gain: 0.07 },
  alert: { type: 'triangle', freq: [660, 440], dur: 0.22, gain: 0.19 },
  select: { type: 'sine', freq: [700, 700], dur: 0.03, gain: 0.06 },
  ok: { type: 'sine', freq: [620, 880], dur: 0.1, gain: 0.1 },
  deny: { type: 'square', freq: [180, 140], dur: 0.09, gain: 0.11 },
};
